import { apiClient } from '../api';
import type { ApiResponse } from '@/types/auth';
import type {
  BillEnvMeta,
  BillPlan,
  BillProduct,
  BillPurchaseResult,
  ElectricityDisco,
  ElectricityQuote,
  MeterValidation,
  SmartcardValidation,
} from '@/types/bills';

class BillsService {
  getElectricityDiscos() {
    return apiClient.get<
      ApiResponse<BillEnvMeta & { discos: ElectricityDisco[] }>
    >('/users/bills/electricity/discos');
  }

  quoteElectricity(serviceId: string, amount: number) {
    return apiClient.get<ApiResponse<{ quote: ElectricityQuote }>>(
      '/users/bills/electricity/quote',
      { params: { serviceId, amount } },
    );
  }

  validateElectricityMeter(payload: {
    serviceId: string;
    meterNumber: string;
    meterType: 'prepaid' | 'postpaid';
  }) {
    return apiClient.post<ApiResponse<MeterValidation & BillEnvMeta>>(
      '/users/bills/electricity/validate-meter',
      payload,
    );
  }

  purchaseElectricity(payload: {
    serviceId: string;
    meterNumber: string;
    meterType: 'prepaid' | 'postpaid';
    amount: number;
    phone: string;
    pin: string;
  }) {
    return apiClient.post<ApiResponse<BillPurchaseResult>>(
      '/users/bills/electricity/purchase',
      payload,
      { timeout: 60_000 },
    );
  }

  getCableProviders() {
    return apiClient.get<ApiResponse<BillEnvMeta & { providers: BillProduct[] }>>(
      '/users/bills/cable-tv/providers',
    );
  }

  getCablePlans(serviceId: string) {
    return apiClient.get<
      ApiResponse<{ product: BillProduct; plans: BillPlan[] }>
    >(`/users/bills/cable-tv/plans/${serviceId}`);
  }

  validateSmartcard(payload: {
    serviceId: string;
    smartcardNumber: string;
    phone: string;
    variationCode?: string;
  }) {
    return apiClient.post<ApiResponse<SmartcardValidation & BillEnvMeta>>(
      '/users/bills/cable-tv/validate-smartcard',
      payload,
    );
  }

  purchaseCableTv(payload: {
    serviceId: string;
    billersCode: string;
    variationCode?: string;
    amount?: number;
    phone: string;
    customerName?: string;
    subscriptionType?: 'change' | 'renew';
    quantity?: number;
    pin: string;
  }) {
    return apiClient.post<ApiResponse<BillPurchaseResult>>(
      '/users/bills/cable-tv/purchase',
      payload,
      { timeout: 60_000 },
    );
  }

  getExamProviders() {
    return apiClient.get<ApiResponse<BillEnvMeta & { providers: BillProduct[] }>>(
      '/users/bills/exams/providers',
    );
  }

  getExamPlans(serviceId: string) {
    return apiClient.get<
      ApiResponse<{ product: BillProduct; plans: BillPlan[] }>
    >(`/users/bills/exams/plans/${serviceId}`);
  }

  validateExamProfile(payload: {
    serviceId: string;
    candidateName: string;
    phone: string;
    variationCode: string;
  }) {
    return apiClient.post<
      ApiResponse<{ customerName: string } & BillEnvMeta>
    >('/users/bills/exams/validate', payload);
  }

  purchaseExam(payload: {
    serviceId: string;
    variationCode: string;
    phone: string;
    candidateName: string;
    quantity?: number;
    pin: string;
  }) {
    return apiClient.post<ApiResponse<BillPurchaseResult>>(
      '/users/bills/exams/purchase',
      payload,
      { timeout: 60_000 },
    );
  }
}

export const billsService = new BillsService();
